import React, { useState, useCallback, useEffect, useRef } from 'react';
import styles from './index.module.less';
import Panel from '@/components/Panel';
import QuestionList from '@/components/QuestionList';
import { useHistory } from 'react-router';
import SearchInput from '@/components/SearchInput';
import { getHotList } from '@/utils/getData';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/Footer';
import PollingEvent from '@/utils/PollingEvent';
import { useDispatch } from 'react-redux';
import { setSearchInfo } from '@/store/search/action';
import { setUser } from '@/store/user/action';
import { helpCenterTracking } from '@/utils/track';
import { getCamelCaseString } from '@/utils/misc';
import Categories from './Categories';

const HOT_LIST = getHotList();

const UserNameKey = {
  id: 'firstName',
  ph: 'name',
  sg: 'name',
}[process.env.SHOPEE_COUNTRY];
const EncryptedKey = 'encrypted';

const Home: React.FC = () => {
  const pollingEventRef = useRef(new PollingEvent());
  const [userName, setUserName] = useState('');
  const [hotList, setHotList] = useState([]);
  const history = useHistory();

  const { t } = useTranslation();

  const dispatch = useDispatch();

  // 进入首页，需要清空 store 中的数据
  useEffect(() => {
    dispatch(
      setSearchInfo({
        value: '',
      }),
    );
  }, [dispatch]);

  const pressSeeMore = useCallback(() => {
    history.push('/hot-question');
    helpCenterTracking({
      operation: 'click',
      target: 'see_more_faq_articles',
    });
  }, [history]);

  const focusInput = useCallback(() => {
    history.push('/search');
    helpCenterTracking({
      operation: 'click',
      target: 'search_bar',
    });
  }, [history]);

  const [isLogin, setIsLogin] = useState(false);

  // if user not login, use mask name
  const formatName = (username, isLogin) => {
    const name = getCamelCaseString(username);
    const maskLen = Math.floor((name?.length + 1) / 2);
    const maskName = name.slice(0, -maskLen) + '*'.repeat(maskLen);
    setUserName(isLogin ? name : maskName);
  };

  const getUserInfo = useCallback(() => {
    try {
      (window as MyWindow).WebViewJavascriptBridge.callHandler(
        'userInfo',
        {},
        (res) => {
          // 有返回就取消轮询，因为存在可能没有 firstName 的场景
          pollingEventRef.current.clear();

          let encrypted = false;
          if (res && res[EncryptedKey]) {
            encrypted = res[EncryptedKey];
          }
          if (res && res[UserNameKey]) {
            const name = encrypted
              ? window.atob(res[UserNameKey])
              : res[UserNameKey];
            formatName(name, res?.isLogin);
            dispatch(setUser({ data: res }));
            helpCenterTracking({
              operation: 'view',
            });
          }

          if (res && res.isLogin) {
            setIsLogin(true);
          }
        },
      );
    } catch (e) {
      return e;
    }
  }, [dispatch]);

  useEffect(() => {
    setHotList(HOT_LIST.slice(0, 5));
    // userName 改为 bridge 获取
    const pollingEvent = pollingEventRef.current;
    pollingEvent.start(50, getUserInfo, 10);
    return () => {
      pollingEvent.clear();
    };
  }, [getUserInfo]);

  useEffect(() => {
    // 动态设置title，需要客户端提供bridge
    document.title = t('title');
  }, [t]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <p className={styles.hello}>
            {t('greeting', {
              name: userName,
              interpolation: { escapeValue: false },
            })}
            ,
          </p>
          <p className={styles.text}>{t('title_home')}</p>
        </div>
        <SearchInput onFocus={() => focusInput()} />
      </div>
      <div className={styles.shadow}></div>
      {hotList && hotList.length > 0 && (
        <div className={styles.panelBox}>
          <Panel
            header={t('faq_full')}
            showFooter={true}
            onPressFooter={() => pressSeeMore()}
          >
            <QuestionList
              list={hotList}
              onTrack={(item, index) =>
                helpCenterTracking({
                  operation: 'click',
                  target: 'faq_article',
                  data: {
                    position: index,
                  },
                })
              }
            />
          </Panel>
        </div>
      )}
      <div className={styles.shadow}></div>
      <Categories />
      <Footer isLogin={isLogin} from="home" />
    </div>
  );
};

export default Home;
